<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import HeroSection from './components/HeroSection.vue'
import ProductsSection from './components/ProductsSection.vue'
import AboutSection from './components/AboutSection.vue'
import ContactSection from './components/ContactSection.vue'
import FooterSection from './components/FooterSection.vue'
import NotificationToast from './components/NotificationToast.vue'
import ScrollProgress from './components/ScrollProgress.vue'
import MobileMenu from './components/MobileMenu.vue'

const navItems = [
  { id: 'home', label: 'Home', href: '#home' },
  { id: 'products', label: 'Products', href: '#products' },
  { id: 'about', label: 'About', href: '#about' },
  { id: 'contact', label: 'Contact', href: '#contact' }
]

const activeSection = ref('home')
const isScrolled = ref(false)
const showBackToTop = ref(false)
const isMobileMenuOpen = ref(false)
const toast = ref({
  visible: false,
  message: '',
  type: 'info'
})

const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  isScrolled.value = scrollTop > 50
  showBackToTop.value = scrollTop > 300
  
  // Update active section based on scroll position
  const sections = ['home', 'products', 'about', 'contact']
  const scrollPosition = scrollTop + 100 // Offset for header height
  
  for (const section of sections) {
    const element = document.getElementById(section)
    if (element) {
      const offsetTop = element.offsetTop
      const height = element.offsetHeight
      if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
        activeSection.value = section
        break
      }
    }
  }
}

const smoothScroll = (href) => {
  const targetId = href.replace('#', '')
  const targetElement = document.getElementById(targetId)
  
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const showToast = (message, type = 'info') => {
  toast.value = {
    visible: true,
    message,
    type
  }
}

const hideToast = () => {
  toast.value.visible = false
}

const toggleMobileMenu = (event) => {
  event.stopPropagation()
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  // Prevent body scroll when menu is open
  if (isMobileMenuOpen.value) {
    document.body.classList.add('menu-open')
  } else {
    document.body.classList.remove('menu-open')
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.classList.remove('menu-open')
}

const handleMobileNavigation = (href) => {
  smoothScroll(href)
  closeMobileMenu()
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // Initial call
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

// Expose toast functionality for child components
defineExpose({
  showToast,
  hideToast
})
</script>

<template>
  <div class="min-h-screen bg-annapolis-navy">
    <!-- Skip to main content link for keyboard navigation -->
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 bg-annapolis-teal text-white px-4 py-2 z-50"
      aria-label="Skip to main content"
    >
      Skip to main content
    </a>
    
    <!-- Scroll Progress Indicator -->
    <!-- <ScrollProgress /> -->
    
    <!-- Sticky Header with Navigation -->
    <header
      role="banner"
      :class="[
        'fixed top-0 left-0 right-0 z-40 transition-all duration-300',
        isScrolled ? 'bg-annapolis-navy/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      ]"
    >
      <nav
        class="container mx-auto px-4 sm:px-6 py-3"
        role="navigation"
        aria-label="Main navigation"
      >
        <div class="flex items-center justify-between">
          <!-- Logo/Brand -->
          <div class="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Annapolis Labs logo - Professional software solutions company"
              class="w-8 h-8 rounded-lg"
              aria-hidden="true"
            />
            <span class="text-white font-bold text-lg">Annapolis Labs</span>
          </div>
          
          <!-- Navigation Links -->
          <ul class="hidden md:flex items-center gap-6">
            <li v-for="item in navItems" :key="item.id">
              <a
                :href="item.href"
                @click.prevent="smoothScroll(item.href)"
                :class="[
                  'text-sm font-medium transition-colors duration-200 px-3 py-2 rounded-md',
                  activeSection === item.id
                    ? 'text-annapolis-teal bg-annapolis-teal/10'
                    : 'text-gray-300 hover:text-annapolis-teal hover:bg-annapolis-teal/10'
                ]"
                :aria-current="activeSection === item.id ? 'location' : undefined"
              >
                {{ item.label }}
              </a>
            </li>
          </ul>
          
          <!-- Mobile Menu Button -->
          <button
            class="md:hidden text-white p-2 rounded-md hover:bg-annapolis-teal/10 transition-colors"
            aria-label="Toggle mobile menu"
            :aria-expanded="isMobileMenuOpen"
            aria-controls="mobile-menu"
            @click.stop="toggleMobileMenu"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>
    </header>
    
    <!-- Mobile Menu -->
    <MobileMenu
      id="mobile-menu"
      :is-open="isMobileMenuOpen"
      :nav-items="navItems"
      :active-section="activeSection"
      @close="closeMobileMenu"
      @navigate="handleMobileNavigation"
    />
    
    <main id="main-content" role="main">
      <section id="home">
        <HeroSection />
      </section>
      <section id="products">
        <ProductsSection />
      </section>
      <section id="about">
        <AboutSection />
      </section>
      <section id="contact">
        <ContactSection @show-toast="showToast" />
      </section>
    </main>
    
    <FooterSection />
    
    <!-- Back to Top Button -->
    <button
      v-show="showBackToTop"
      @click="scrollToTop"
      class="fixed bottom-6 right-6 z-40 w-12 h-12 bg-annapolis-teal hover:bg-annapolis-teal/90 text-white rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-annapolis-teal focus:ring-offset-2 focus:ring-offset-annapolis-navy"
      aria-label="Back to top"
    >
      <svg class="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
    
    <!-- Notification Toast -->
    <NotificationToast
      v-if="toast.visible"
      :message="toast.message"
      :type="toast.type"
      @hide="hideToast"
    />
  </div>
</template>

<style>
html {
  scroll-behavior: smooth;
}

/* Add padding to main content to account for sticky header */
main {
  padding-top: 0;
}

/* Smooth transitions for all interactive elements */
a, button {
  transition: all 0.2s ease-in-out;
}

/* Prevent body scroll when mobile menu is open */
body.menu-open {
  overflow: hidden;
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  
  a, button {
    transition: none;
  }
}
</style>
